import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/testimonials';
import { teamMembers } from '@/mocks/team';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9tgq3dca23r0tr1aiqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormError(serverMsg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于性事咨询员',
    description: '性事咨询员是一个专业、私密的两性健康咨询服务平台，致力于为每一位来访者提供科学、温暖、可信赖的咨询服务。',
    url: 'https://xingshizixun.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '性事咨询员',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main className="pb-16">
        <section className="pt-10 md:pt-14 pb-16 px-4 md:px-6 max-w-4xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6 text-center">
            关于我们
          </h1>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12 leading-relaxed">
            性事咨询员是一个专业、私密的两性健康咨询服务平台，
            致力于为每一位来访者提供科学、温暖、可信赖的咨询服务
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌故事</h2>
              <div className="space-y-3 text-foreground-700 leading-relaxed">
                <p>
                  性事咨询员创立于对两性健康领域深刻理解的初心之上。我们的创始团队由资深性治疗师、临床心理学家和男科医学专家组成，在多年的临床实践中，他们看到了太多因为缺乏正确信息和专业支持而长期受苦的人。
                </p>
                <p>
                  在中国，谈论「性」仍然是一个充满禁忌的话题。许多人带着困惑和痛苦独自承受，不知道去哪里寻求帮助，也不确定自己的问题是否「正常」。性事咨询员的诞生，正是为了打破这种沉默与孤独。
                </p>
                <p>
                  我们相信，每个人——无论性别、年龄、性取向——都值得拥有健康的性生活和满意的亲密关系。这不是奢侈品，而是人类幸福的基本组成部分。
                </p>
              </div>
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">我们的理念</h2>
              <div className="space-y-3 text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">科学为本：</strong>所有的咨询服务都建立在循证医学和心理学研究的基础之上，绝不贩卖焦虑或提供未经证实的「偏方」。
                </p>
                <p>
                  <strong className="text-foreground-900">隐私至上：</strong>我们深知来访者的信任来之不易，因此将隐私保护作为所有服务的基石。匿名咨询、加密通讯、严格的保密协议——您的故事，只有您和咨询师知道。
                </p>
                <p>
                  <strong className="text-foreground-900">温暖陪伴：</strong>我们不走「冷冰冰的专家」路线。每一位咨询师都经过人文关怀维度的严格筛选，确保您在专业指导之外，还能感受到被理解、被接纳的温暖。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 md:px-6" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
              来访者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/50">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    「{t.content}」
                  </p>
                  <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 md:px-6 max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
            顾问资质
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {teamMembers.map((member) => (
              <div key={member.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70 flex gap-5">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-user-3-line text-2xl text-primary-600"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">{member.name}</h3>
                  <p className="text-sm text-primary-600 font-medium">{member.title}</p>
                  <p className="text-xs text-foreground-500 mt-1">{member.credentials}</p>
                  <p className="text-xs text-foreground-500">{member.experience}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="py-16 px-4 md:px-6" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10 leading-relaxed">
              填写以下表单，我们的顾问将在24小时内与您联系。
              <br />
              所有信息严格保密，请放心填写。
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5 p-8 rounded-xl bg-background-50 border border-background-200/70"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  您的称呼 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的称呼（可使用昵称）"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的邮箱地址"
                />
              </div>

              <div>
                <label htmlFor="contact-service" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  咨询方向
                </label>
                <select
                  id="contact-service"
                  name="service"
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                >
                  <option value="">请选择您感兴趣的咨询方向</option>
                  <option value="性健康教育">性健康教育</option>
                  <option value="亲密关系指导">亲密关系指导</option>
                  <option value="性功能困扰">性功能困扰</option>
                  <option value="性心理健康">性心理健康</option>
                  <option value="婚姻家庭咨询">婚姻家庭咨询</option>
                  <option value="青春期性教育">青春期性教育</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请简要描述您想咨询的问题（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交咨询预约'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-center" style={{ color: 'oklch(var(--secondary-600))' }}>
                  提交成功！我们的顾问将在24小时内通过您提供的邮箱与您联系，请留意查收。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-center text-red-500">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}